import { LuRedo2 } from 'react-icons/lu';
import { TagPill } from './TagPill';
import type { VisibleOption } from '../hooks/useStanMultiSelect';
import dropdownStyles from '../../StanDropdown/Menu/StanDropdownMenu.module.css';
import localStyles from '../StanMultiSelect.module.css';
import { cn } from '@/lib/utils/cn';

// Helper function to render create option
const renderCreateOption = (opt: VisibleOption) => (
    <div className={localStyles.optionCreate}>
        + Press enter to create a new tag <span className={localStyles.optionCreateLabel}>&quot;{opt.label}&quot;</span>
    </div>
);

// Helper function to render regular option
const renderRegularOption = (opt: VisibleOption, onSelect: (opt: VisibleOption) => void) => (
    <div className={localStyles.optionContent}>
        <TagPill label={opt.label} onRemove={() => onSelect(opt)} disabled={false} showRemove={false} />
        <div className={localStyles.optionHint}>
            <LuRedo2 className={localStyles.optionHintIcon} /> Enter to select
        </div>
    </div>
);

export const OptionsList: React.FC<{
    listboxId: string;
    options: VisibleOption[];
    activeIndex: number;
    onHover: (i: number) => void;
    onSelect: (opt: VisibleOption) => void;
}> = ({ listboxId, options, activeIndex, onHover, onSelect }) => {
    return (
        <ul id={listboxId} role='listbox' className=''>
            {options.length === 0 ? (
                <li className={localStyles.noResults} aria-disabled>
                    No matches
                </li>
            ) : (
                options.map((opt, i) => {
                    const isCreate = Boolean(opt.isCreatable);
                    const isActive = i === activeIndex;

                    return (
                        <li
                            key={`${opt.id}-${i + 1}`}
                            id={`${listboxId}-opt-${i}`}
                            role='option'
                            aria-selected={isActive}
                            className={cn(
                                dropdownStyles.item,
                                localStyles.option,
                                isActive && localStyles.optionActive
                            )}
                            // onMouseEnter syncs keyboard and mouse navigation:
                            // - Updates activeIndex JS state (CSS :hover can't do this)
                            // - Keeps aria-selected in sync for screen readers
                            // - Ensures Enter key selects the hovered option
                            // - Provides consistent UX when switching between input methods
                            onMouseEnter={() => onHover(i)}
                            onMouseDown={e => e.preventDefault()} // Prevent input blur when clicking on options
                            onClick={() => onSelect(opt)}
                        >
                            {isCreate ? renderCreateOption(opt) : renderRegularOption(opt, onSelect)}
                        </li>
                    );
                })
            )}
        </ul>
    );
};
